import type { ApolloQueryResult } from 'apollo-client';

import type {
	ActiveInlineCommentsQueryType,
	CommentLocation,
} from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.ts';
import type { InlineCommentQueryType } from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery.ts';
import { CommentType } from '@confluence/comments-data/entry-points/useCommentsData.ts';
import type {
	CommentData,
	CommentsDataMap,
	ReplyData,
} from '@confluence/comments-data/entry-points/useCommentsData.ts';
import { InlineCommentQuery } from '@confluence/inline-comments-queries/entry-points/InlineCommentQuery.graphql.ts';
import { ActiveInlineCommentsQuery } from '@confluence/inline-comments-queries/entry-points/ActiveInlineCommentsQuery.graphql.ts';
import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { fg } from '@confluence/feature-gating';

export const refreshCommentThread = (
	contentId: string,
	markerRef: string,
): Promise<ApolloQueryResult<any>> => {
	if (!fg('confluence_frontend_content_wrapper')) {
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		return getApolloClient().query<InlineCommentQueryType>({
			query: InlineCommentQuery,
			fetchPolicy: 'network-only',
			variables: {
				pageId: contentId,
				annotationId: markerRef,
				contentStatus: ['DRAFT', 'CURRENT'],
			},
		});
	} else {
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		return getApolloClient().query<ActiveInlineCommentsQueryType>({
			query: ActiveInlineCommentsQuery,
			fetchPolicy: 'network-only',
			variables: {
				pageId: contentId,
				inlineMarkerRefList: [markerRef],
				contentStatus: ['DRAFT', 'CURRENT'],
			},
		});
	}
};

export const fetchCommentThread = async (
	contentId: string,
	markerRef: string,
): Promise<InlineCommentQueryType | ActiveInlineCommentsQueryType> => {
	if (!fg('confluence_frontend_content_wrapper')) {
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		const commentThreadDataResult = await getApolloClient().query<InlineCommentQueryType>({
			query: InlineCommentQuery,
			fetchPolicy: 'network-only',
			variables: {
				pageId: contentId,
				annotationId: markerRef,
				contentStatus: ['DRAFT', 'CURRENT'],
			},
		});
		return commentThreadDataResult.data;
	} else {
		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		const commentThreadDataResult = await getApolloClient().query<ActiveInlineCommentsQueryType>({
			query: ActiveInlineCommentsQuery,
			fetchPolicy: 'network-only',
			variables: {
				pageId: contentId,
				inlineMarkerRefList: [markerRef],
				contentStatus: ['DRAFT', 'CURRENT'],
			},
		});
		return commentThreadDataResult.data;
	}
};

export const handleAddNewCommentThread = async ({
	contentId,
	markerRef,
	addNewCommentThreads,
}: {
	contentId: string | undefined;
	markerRef: string;
	addNewCommentThreads: ((newCommentThreads: CommentsDataMap) => void) | undefined;
}) => {
	const commentThreadDataResult = await fetchCommentThread(contentId ?? '', markerRef);
	const comment = commentThreadDataResult?.comments?.nodes?.[0];
	if (comment) {
		addNewCommentThreads &&
			addNewCommentThreads({
				inline: {
					[markerRef]: {
						...comment,
						isUnread: true,
						isOpen: true,
						wasRemovedByAnotherUser: false,
						type: CommentType.INLINE,
					} as CommentData,
				},
				general: {},
			});
	}
};

export const handleAddedReplyInCommentThread = async ({
	contentId,
	markerRef,
	commentId,
	addReplyToCommentThread,
	commentType,
	storedCommentData,
	setStoredCommentData,
}: {
	contentId: string | undefined;
	markerRef: string;
	commentId: string;
	addReplyToCommentThread:
		| ((parentMarkerRef: string, reply: ReplyData, commentType: CommentType) => void)
		| undefined;
	commentType: CommentType;
	storedCommentData: CommentData | null;
	setStoredCommentData?: (commentData: any) => void;
}) => {
	const commentThreadDataResult = await fetchCommentThread(contentId ?? '', markerRef);
	const comment = commentThreadDataResult?.comments?.nodes?.[0];
	const replies = comment?.replies;
	const reply = replies?.filter((r) => r?.id === commentId)[0];
	if (reply) {
		// Update storedCommentData if it exists by adding the new reply
		if (storedCommentData && setStoredCommentData) {
			const updatedCommentData = {
				...storedCommentData,
				replies: [...(storedCommentData.replies || []), reply],
			};

			// Only update if inlineMarkerRef matches between stored and fetched comment
			if (
				(storedCommentData.location as CommentLocation)?.inlineMarkerRef ===
				(comment?.location as CommentLocation)?.inlineMarkerRef
			) {
				setStoredCommentData(updatedCommentData);
			}
		}

		addReplyToCommentThread &&
			addReplyToCommentThread(
				markerRef,
				{
					...reply,
					isUnread: true,
				} as ReplyData,
				commentType,
			);
	}
};

export const handleEditedComment = async ({
	contentId,
	markerRef,
	commentId,
	updateCommentBody,
}: {
	contentId: string | undefined;
	markerRef: string;
	commentId: string;
	updateCommentBody: (
		commentId: string,
		threadKey: string,
		newBodyValue: string,
		commentType: CommentType,
		displayName?: string | null,
	) => void;
}) => {
	const commentThreadDataResult = await fetchCommentThread(contentId ?? '', markerRef);
	const comment = commentThreadDataResult?.comments?.nodes?.[0];

	const targetComment =
		comment?.id === commentId ? comment : comment?.replies?.find((r) => r?.id === commentId);

	if (targetComment && updateCommentBody) {
		updateCommentBody(
			commentId,
			markerRef,
			targetComment.body.value,
			CommentType.INLINE,
			targetComment.version.by?.displayName,
		);
	}
};
